import { Link, Navigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Sun, Moon } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useTheme } from '@context/ThemeContext';
import { AuthGradientBackground } from '@components/auth/AuthGradientBackground';
import { WELCOME_HEADLINE, WELCOME_SUBLINE } from '@components/auth/AuthWelcomeAside';
import { prefersAuthDesktop } from '@utils/authEntryPaths';

const WelcomePage = () => {
  const location = useLocation();
  const { dark, toggleTheme } = useTheme();
  const forwardState = location.state;

  if (prefersAuthDesktop()) {
    return <Navigate to="/register" replace state={forwardState} />;
  }

  return (
    <div className="relative flex min-h-screen min-w-0 flex-col">
      <AuthGradientBackground className="min-h-screen flex flex-col">
        <div className="relative z-10 flex items-center justify-between px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-semibold text-white/90 transition-colors hover:text-white"
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
            Back
          </Link>
          <button type="button" onClick={toggleTheme} className="theme-toggle !bg-white/15 !text-white hover:!bg-white/25" aria-label="Toggle theme">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-10 pt-4 text-center sm:px-8">
          <h1 className="mb-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{WELCOME_HEADLINE}</h1>
          <p className="mb-10 max-w-sm text-sm leading-relaxed text-white/85 sm:max-w-md sm:text-base">{WELCOME_SUBLINE}</p>
          <div className="mb-12 flex justify-center sm:mb-14">
            <Logo size="hero" showText darkSurroundings className="!flex-col gap-4 sm:!flex-row sm:items-center" to="/" />
          </div>

          <div className="mt-auto flex w-full max-w-sm flex-col gap-3 sm:max-w-md">
            <Link
              to="/login"
              state={forwardState}
              className="flex w-full items-center justify-center rounded-full bg-gradient-to-r from-sky-400 via-brand-500 to-violet-600 px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-900/20 transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
            >
              Log in
            </Link>
            <Link
              to="/register"
              state={forwardState}
              className="flex w-full items-center justify-center rounded-full border-2 border-white/80 bg-white/10 px-8 py-4 text-base font-bold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20 active:scale-[0.98]"
            >
              Create account
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/50">
            <Link to="/privacy" className="font-medium text-white/70 underline-offset-2 hover:text-white hover:underline">
              Privacy
            </Link>
            <Link to="/terms" className="font-medium text-white/70 underline-offset-2 hover:text-white hover:underline">
              Terms
            </Link>
          </div>
        </div>
      </AuthGradientBackground>
    </div>
  );
};

export default WelcomePage;
